import { AddReactionIcon, DeleteIcon, ReplyIcon, RetryIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import { Avatar } from '@components/Avatar'
import Spinner from '@components/Spinner'
import { usePortalDropdownPosition } from '@hooks/usePortalDropdownPosition'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChatCardIcon, ChatGlyph, StreamChatCard } from './ChatCard'
import {
	ActionBar,
	ActionButton,
	AddAsIdeaButton,
	AuthorName,
	AvatarSlot,
	BodyText,
	Bubble,
	CardsStack,
	EmojiButton,
	FailedButton,
	LinkPreviewActions,
	LinkPreviewCard,
	LinkPreviewLink,
	LinkPreviewSite,
	LinkPreviewText,
	LinkPreviewTitle,
	Mention,
	MessageRow,
	MessageStack,
	MoreBadge,
	PHOTO_GRID_WIDTH,
	PhotoGrid,
	PhotoRow,
	PhotoTile,
	QuoteAuthor,
	QuoteButton,
	QuoteGlyph,
	QuoteSnippet,
	QuoteText,
	ReactionChip,
	ReactionPalette,
	Reactions,
	TextLink,
	Time,
	TimeSpacer,
} from './ChatMessage.styles'
import {
	CHAT_REACTIONS,
	canAddAsIdea,
	lonePhotoRatio,
	photoGrid,
	reactionEmoji,
	sentMessageId,
	textSegments,
	type ChatCard,
	type ChatMessageRow,
	type ChatPhoto,
	type ChatQuotePresentation,
	type ChatReaction,
	type ChatReactionChoice,
} from './core'

/** What a Message's bubble can be asked to do; a surface leaves out what it doesn't offer. */
export interface ChatMessageHandlers {
	/** A card was clicked, to open what it tags. */
	onOpenCard: (card: ChatCard) => void
	/** The quote was clicked, with the id of the Message it quotes. */
	onOpenQuote: (messageId: number) => void
	/** A reaction chip, or one of the six in the palette: mine comes off, another becomes mine. */
	onReact: (reaction: ChatReactionChoice, messageId: number) => void
	/** "+ Add as idea" on the link preview. */
	onAddLinkAsIdea: (messageId: number) => void
	/** A photo, to see the Message's photos full screen from it. */
	onOpenPhotos: (photos: ChatPhoto[], startIndex: number) => void
	onReply?: (messageId: number) => void
	/** Only for my own sent Messages. */
	onDelete?: (messageId: number) => void
	onRetry?: (clientId: string) => void
	onDiscard?: (clientId: string) => void
	/** The bubble itself was clicked, outside its cards, links and buttons — a Discussion opens the Chat on it. */
	onOpenMessage?: (messageId: number) => void
}

interface ChatMessageProps extends ChatMessageHandlers {
	row: ChatMessageRow
	/** Draws the eye to the bubble, as when the Chat opens on it. */
	flash?: boolean
}

const QuoteView: React.FC<{ quote: ChatQuotePresentation; mine: boolean; onOpen: (messageId: number) => void }> = ({
	quote,
	mine,
	onOpen,
}) => (
	<QuoteButton
		type="button"
		$inMyBubble={mine}
		$quotesMine={quote.isMine}
		disabled={quote.isRemoved}
		onClick={() => onOpen(quote.messageId)}
		aria-label={`Show the message ${quote.authorName} wrote`}
	>
		<QuoteAuthor $quotesMine={quote.isMine}>{quote.authorName}</QuoteAuthor>
		<QuoteText>
			{quote.glyph && (
				<QuoteGlyph>
					<ChatGlyph glyph={quote.glyph} category={quote.category} fontSize={iconSizes.xs} />
				</QuoteGlyph>
			)}
			<QuoteSnippet $removed={quote.isRemoved}>{quote.snippet}</QuoteSnippet>
		</QuoteText>
	</QuoteButton>
)

/** A Message's photos: one at its own shape; more two to a row, the last counting any past four. */
const PhotoGridView: React.FC<{ photos: ChatPhoto[]; onOpen: (index: number) => void }> = ({ photos, onOpen }) => {
	const { rows, hidden } = photoGrid(photos.length)
	const side = (PHOTO_GRID_WIDTH - 3) / 2
	const lastIndex = rows[rows.length - 1]?.at(-1)
	return (
		<PhotoGrid>
			{rows.map(indexes => (
				<PhotoRow key={indexes[0]}>
					{indexes.map(index => {
						const photo = photos[index]
						const height = photos.length === 1 ? Math.round(PHOTO_GRID_WIDTH * lonePhotoRatio(photo)) : side
						return (
							<PhotoTile
								key={photo.id}
								type="button"
								$height={height}
								onClick={() => onOpen(index)}
								aria-label={`Photo ${index + 1} of ${photos.length}`}
							>
								<img src={photo.url} alt="" loading="lazy" />
								{hidden > 0 && index === lastIndex && <MoreBadge>+{hidden}</MoreBadge>}
							</PhotoTile>
						)
					})}
				</PhotoRow>
			))}
		</PhotoGrid>
	)
}

/** The six reactions under the button that opens them; mine is marked. */
const ReactionPicker: React.FC<{ mine: ChatReaction | null; onPick: (reaction: ChatReactionChoice) => void }> = ({
	mine,
	onPick,
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const anchorRef = useRef<HTMLButtonElement>(null)
	const paletteRef = useRef<HTMLDivElement>(null)
	const position = usePortalDropdownPosition(anchorRef, isOpen)

	useEffect(() => {
		if (!isOpen) return
		const close = (e: MouseEvent | KeyboardEvent) => {
			if (e instanceof KeyboardEvent ? e.key === 'Escape' : !paletteRef.current?.contains(e.target as Node))
				setIsOpen(false)
		}
		document.addEventListener('mousedown', close)
		document.addEventListener('keydown', close)
		return () => {
			document.removeEventListener('mousedown', close)
			document.removeEventListener('keydown', close)
		}
	}, [isOpen])

	return (
		<>
			<ActionButton
				ref={anchorRef}
				type="button"
				onClick={() => setIsOpen(open => !open)}
				aria-label="React"
				aria-expanded={isOpen}
				title="React"
			>
				<AddReactionIcon sx={{ fontSize: iconSizes.md }} />
			</ActionButton>
			{isOpen &&
				position &&
				createPortal(
					<ReactionPalette ref={paletteRef} $top={position.top} $left={position.left} role="menu">
						{CHAT_REACTIONS.map(reaction => (
							<EmojiButton
								key={reaction}
								type="button"
								role="menuitemcheckbox"
								aria-checked={reaction === mine}
								aria-label={reaction}
								$mine={reaction === mine}
								onClick={() => {
									setIsOpen(false)
									onPick(reaction)
								}}
							>
								{reactionEmoji(reaction)}
							</EmojiButton>
						))}
					</ReactionPalette>,
					document.body,
				)}
		</>
	)
}

/**
 * One Message's bubble, as the Chat's stream and a Discussion both show it. Mine sit on
 * the right; others' on the left, with the author's name over the first of a run and
 * their avatar beside the last. A reply shows what it quotes, photos and the link preview
 * sit over the text, tagged members are highlighted in it, tagged resources show as cards
 * under it, and reactions as chips under the bubble. The time sits in the bubble's bottom
 * corner, beside the text's last line when there's room. Hovering (or, on touch, clicking
 * the bubble) brings up what can be done to it. Ported from iOS `ChatMessageView`.
 */
const ChatMessage: React.FC<ChatMessageProps> = ({ row, flash = false, ...handlers }) => {
	const [picked, setPicked] = useState(false)
	const messageId = sentMessageId(row)
	const mine = row.isMine
	const hasText = row.text !== ''
	const isBubbleless =
		!hasText && (row.cards.length > 0 || row.photos.length > 0) && row.quote === null && row.linkPreview === null
	// Text that ends the bubble keeps room for the time on its last line; anything else
	// ends it with a row of its own for the time.
	const isTimeBesideText = hasText && row.cards.length === 0
	const myReaction = row.reactions.find(r => r.reactedByMe)?.reaction ?? null

	const offersActions = !row.isRemoved && row.delivery.status !== 'sending'
	const isFailed = row.delivery.status === 'failed'

	const handleBubbleClick = (e: React.MouseEvent) => {
		if ((e.target as HTMLElement).closest('button, a')) return
		if (handlers.onOpenMessage && messageId !== null) {
			handlers.onOpenMessage(messageId)
			return
		}
		// Touch has no hover: a click on the bubble brings its actions up instead.
		if (window.matchMedia?.('(hover: none)').matches) setPicked(current => !current)
	}

	// Double-clicking a Message hearts it, or takes my heart off, as its ❤️ chip would
	// (iOS `ChatMessageRow.doubleTapAction`). Not once it's deleted or before the server
	// has it, not on what a click already does something with — a link, a button, or the
	// cards and link preview marked `data-own-click`, padding included — and not in a
	// Discussion, where a click opens the Chat.
	const heartsOnDoubleClick = messageId !== null && !row.isRemoved && !handlers.onOpenMessage
	const handleBubbleDoubleClick = (e: React.MouseEvent) => {
		if (!heartsOnDoubleClick || (e.target as HTMLElement).closest('button, a, [data-own-click]')) return
		handlers.onReact('Heart', messageId)
	}
	// The double-click is the heart's, not a word selection's; a triple-click still selects.
	const handleBubbleMouseDown = (e: React.MouseEvent) => {
		if (heartsOnDoubleClick && e.detail === 2) e.preventDefault()
	}

	return (
		<MessageRow $mine={mine} $opensRun={row.authorName !== null} data-message-id={messageId ?? undefined}>
			{!mine && <AvatarSlot>{row.avatar && <Avatar user={row.avatar} size="md" title={row.avatar.firstName} />}</AvatarSlot>}
			<MessageStack $mine={mine} $hasCards={row.cards.length > 0}>
				{row.authorName && <AuthorName>{row.authorName}</AuthorName>}
				<Bubble
					$mine={mine}
					$removed={row.isRemoved}
					$bubbleless={isBubbleless}
					$sending={row.delivery.status === 'sending'}
					$flash={flash}
					$hasQuote={row.quote !== null}
					$hasCards={row.cards.length > 0}
					$timeRow={!isTimeBesideText}
					onClick={handleBubbleClick}
					onDoubleClick={handleBubbleDoubleClick}
					onMouseDown={handleBubbleMouseDown}
				>
					{row.quote && <QuoteView quote={row.quote} mine={mine} onOpen={handlers.onOpenQuote} />}
					{row.photos.length > 0 && (
						<PhotoGridView photos={row.photos} onOpen={index => handlers.onOpenPhotos(row.photos, index)} />
					)}
					{row.linkPreview && (
						<LinkPreviewCard data-own-click>
							<LinkPreviewLink href={row.linkPreview.url} target="_blank" rel="noopener noreferrer">
								<ChatCardIcon glyph="link" thumbnail={row.linkPreview.image} size={64} />
								<LinkPreviewText>
									<LinkPreviewTitle>{row.linkPreview.title ?? row.linkPreview.url}</LinkPreviewTitle>
									{row.linkPreview.siteName && <LinkPreviewSite>{row.linkPreview.siteName}</LinkPreviewSite>}
								</LinkPreviewText>
							</LinkPreviewLink>
							{(canAddAsIdea(row) || row.isAddingIdea) && messageId !== null && (
								<LinkPreviewActions>
									{row.isAddingIdea ? (
										<Spinner size={20} />
									) : (
										<AddAsIdeaButton type="button" onClick={() => handlers.onAddLinkAsIdea(messageId)}>
											+ Add as idea
										</AddAsIdeaButton>
									)}
								</LinkPreviewActions>
							)}
						</LinkPreviewCard>
					)}
					{hasText && (
						<BodyText $removed={row.isRemoved}>
							{row.isRemoved
								? row.text
								: textSegments(row.text, row.mentions).map((segment, index) => {
										if (segment.type === 'mention')
											return (
												<Mention key={index} $mine={mine}>
													{segment.text}
												</Mention>
											)
										if (segment.type === 'link')
											return (
												<TextLink key={index} href={segment.href} target="_blank" rel="noopener noreferrer">
													{segment.text}
												</TextLink>
											)
										return <React.Fragment key={index}>{segment.text}</React.Fragment>
									})}
							{isTimeBesideText && <TimeSpacer aria-hidden>{row.time}</TimeSpacer>}
						</BodyText>
					)}
					{row.cards.length > 0 && (
						<CardsStack data-own-click>
							{row.cards.map(card => (
								<StreamChatCard key={`${card.kind}-${card.id}`} card={card} onOpen={handlers.onOpenCard} />
							))}
						</CardsStack>
					)}
					<Time $bubbleless={isBubbleless}>{row.time}</Time>
				</Bubble>
				{row.reactions.length > 0 && messageId !== null && (
					<Reactions>
						{row.reactions.map(({ reaction, count, reactedByMe }) =>
							reaction === 'Other' ? null : (
								<ReactionChip
									key={reaction}
									as="button"
									type="button"
									$mine={reactedByMe}
									aria-pressed={reactedByMe}
									aria-label={`${reactionEmoji(reaction)} ${count}${reactedByMe ? ', yours' : ''}`}
									onClick={() => handlers.onReact(reaction, messageId)}
								>
									{reactionEmoji(reaction)} {count}
								</ReactionChip>
							),
						)}
					</Reactions>
				)}
				{isFailed && handlers.onRetry && (
					<FailedButton type="button" onClick={() => handlers.onRetry?.(row.clientId)}>
						Not sent. Click to retry
					</FailedButton>
				)}
				{offersActions && (
					<ActionBar $mine={mine} $shown={picked}>
						{messageId !== null && (
							<ReactionPicker mine={myReaction} onPick={reaction => handlers.onReact(reaction, messageId)} />
						)}
						{messageId !== null && handlers.onReply && (
							<ActionButton type="button" onClick={() => handlers.onReply?.(messageId)} aria-label="Reply" title="Reply">
								<ReplyIcon sx={{ fontSize: iconSizes.md }} />
							</ActionButton>
						)}
						{isFailed && handlers.onRetry && (
							<ActionButton type="button" onClick={() => handlers.onRetry?.(row.clientId)} aria-label="Retry" title="Retry">
								<RetryIcon sx={{ fontSize: iconSizes.md }} />
							</ActionButton>
						)}
						{mine && messageId !== null && handlers.onDelete && (
							<ActionButton type="button" onClick={() => handlers.onDelete?.(messageId)} aria-label="Delete" title="Delete">
								<DeleteIcon sx={{ fontSize: iconSizes.md }} />
							</ActionButton>
						)}
						{isFailed && handlers.onDiscard && (
							<ActionButton
								type="button"
								onClick={() => handlers.onDiscard?.(row.clientId)}
								aria-label="Delete"
								title="Delete"
							>
								<DeleteIcon sx={{ fontSize: iconSizes.md }} />
							</ActionButton>
						)}
					</ActionBar>
				)}
			</MessageStack>
		</MessageRow>
	)
}

export default ChatMessage
